import { Component, ElementRef, TemplateRef, Type, computed, contentChild, contentChildren, effect, input, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AnnouncementBaseViewComponent, BaseViewComponent, Field, GetAnimationDuration, IconItem, LocalStorageKey, OverflowSidebarItem, Scope, Storage, routeAnimations } from '@sb/library/base';
import { NavigationItemState } from '@sb/library/data-models';
import { filter } from 'rxjs/operators';
import { LoadingSpinnerScope } from '../loading/spinner/spinner.component';
import { animateText } from '../sidebar/sidebar-animations';
import { SideBarItemComponent, SidebarScope } from '../sidebar/sidebar-item/sidebar-item.component';

export interface HeaderBreadcrumb {
    label: string;
    url: string;
    icon?: IconItem;
    queryParams?: Record<string, any>;
    fragment?: string;
}

export class AppBarHeaderScope extends Scope {
    SetTabTemplateRef? = Field<(value: TemplateRef<any> | null) => void>();
    SetCurrentBreadCrumbRef? = Field<(value: TemplateRef<any> | null) => void>();
    /** Overrides the label of the crumb at the next available tail position (the deepest not-yet-overridden crumb). Call once per crumb, in shallow-to-deep order, to override multiple crumbs at once (e.g. a folder hierarchy). Passing null clears all current overrides. */
    SetCurrentBreadCrumbLabel? = Field<(value: string | null) => void>();
    /** Clears all breadcrumb label overrides previously set via SetCurrentBreadCrumbLabel. Call before re-building a sequence of overrides (e.g. at the start of a folder navigation update). */
    ClearCurrentBreadCrumbLabels? = Field<() => void>();
}

export class LayoutContainerScope extends AppBarHeaderScope {
    HandleNavigationEnd = Field<(event: NavigationEnd, sidebarItemList: readonly SideBarItemComponent[]) => void>();
    /** Updates the current breadcrumb's fragment in place (e.g. when only the URL fragment changes and the route itself hasn't). */
    UpdateBreadcrumbFragment = Field<(fragment: string | undefined) => void>();
    ResolveRouteAnimation = Field<(componentType: Type<any>) => string>();
    ForceDefaultAnimation = Field<() => void>();
    SetCurrentComponentType = Field<(value: Type<any> | null) => void>();
    currentHeaderTitle = Field<() => string>();
    currentHeaderBreadcrumb = Field<() => HeaderBreadcrumb[]>();
    tabTemplateRef = Field<() => TemplateRef<any> | null>();
    currentBreadCrumbTemplateRef = Field<() => TemplateRef<any> | null>();

}

@Component({
    selector: 'lib-application-layout-container',
    templateUrl: './application-layout-container.component.html',
    styleUrls: ['./application-layout-container.component.scss'],
    animations: [routeAnimations, animateText]
})
export class ApplicationLayoutContainerComponent extends BaseViewComponent implements SidebarScope, LoadingSpinnerScope {

    Scope = this.SetScope({ Implements: [SidebarScope, LoadingSpinnerScope], Requires: LayoutContainerScope });

    readonly links = contentChild.required<TemplateRef<any>>('links');
    readonly sidebarItems = contentChildren(SideBarItemComponent);
    readonly sidebarLinksRef = viewChild<ElementRef<HTMLElement>>('sidebarLinks');
    readonly sidebarElRef = viewChild<ElementRef<HTMLElement>>('sidebarEl');
    readonly announcementRef = viewChild<unknown, ElementRef<HTMLElement>>('announcementRef', { read: ElementRef });

    readonly announcement = contentChild<AnnouncementBaseViewComponent>(AnnouncementBaseViewComponent);


    readonly storageKey = input<LocalStorageKey<boolean>>();
    readonly showLogo = input<boolean>(true);

    readonly onLinkTextChange = signal(false);
    readonly overflowItems = signal<OverflowSidebarItem[]>([]);
    readonly announcementHeight = signal(0);
    readonly sideNavOpen = signal(false);
    readonly sideNavClosing = signal(false);
    routeAnimationState = 'initial';

    get routeAnimationDuration(): number {
        return GetAnimationDuration(this.isMobile || this.isTablet);
    }

    readonly currentHeaderTitle = computed(() => this.Scope.currentHeaderTitle());
    readonly currentHeaderBreadcrumb = computed(() => this.Scope.currentHeaderBreadcrumb());
    /** Only show the first crumb, a collapsed menu for anything in between, and the last crumb when there are more than 3 levels. */
    readonly breadcrumbIsCollapsed = computed(() => this.currentHeaderBreadcrumb().length > 3);
    /** On tablet, force collapse breadcrumbs when there are 3+ items (max 2 levels: crumb > crumb or ... > crumb) */
    readonly breadcrumbIsTabletCollapsed = computed(() => this.isTablet && this.currentHeaderBreadcrumb().length > 2);
    /** On mobile, force collapse breadcrumbs when there are 3+ items (max 1 full breadcrumb in below bar after header shows first) */
    readonly breadcrumbIsMobileCollapsed = computed(() => this.isMobile && this.currentHeaderBreadcrumb().length > 2);
    readonly breadcrumbFirst = computed(() => this.currentHeaderBreadcrumb()[0]);
    readonly breadcrumbMiddle = computed(() => this.currentHeaderBreadcrumb().slice(1, -1));
    readonly breadcrumbLast = computed(() => this.currentHeaderBreadcrumb()[this.currentHeaderBreadcrumb().length - 1]);
    /** Previous breadcrumb (one level up) */
    readonly breadcrumbPrevious = computed(() => {
        const len = this.currentHeaderBreadcrumb().length;
        return len > 1 ? this.currentHeaderBreadcrumb()[len - 2] : undefined;
    });
    /** Show title with chevron when there are no breadcrumbs but title and template both exist */
    readonly showTitleWithTemplate = computed(() => 
        this.currentHeaderBreadcrumb().length === 0 && 
        this.currentHeaderTitle() && 
        this.Scope.currentBreadCrumbTemplateRef()
    );

    private _resizeObserver?: ResizeObserver;
    private _announcementResizeObserver?: ResizeObserver;
    private _overflowUpdateFrame: number | null = null;
    private _sidenavStateTimer?: ReturnType<typeof setTimeout>;
    private _sidenavAnimationTimer?: ReturnType<typeof setTimeout>;

    private _previousRoutePath: string | null = null;
    private _previousHadQueryParams: boolean = false;
    private _previousFragment: string | undefined = undefined;

    constructor(
        private _Router: Router,
    ) {
        super();

        effect(() => {
            // Read every input signal so the effect reruns when any of them changes, not just when the item list itself changes.
            this.sidebarItems().forEach(item => {
                item.routerLink();
                item.navigationTitle();
                item.icon();
                item.badgeText();
                item.fragment();
                item.queryParams();
                item.visibility();
                item.linkActive();
            });
            this.UpdateOverflowItems();
        }, { allowSignalWrites: true });

        this._subscriptions.push(_Router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(event => {
                const navigationEnd = event as NavigationEnd;
                const rawUrl = navigationEnd.urlAfterRedirects || navigationEnd.url;
                const [urlWithoutFragment, fragment] = rawUrl.split('#');
                const [routePath, queryString] = urlWithoutFragment.split('?');
                const hasQueryParams = !!queryString;
                const isSameRoute = this._previousRoutePath === routePath;
                const queryParamsRemoved = this._previousHadQueryParams && !hasQueryParams;

                if (isSameRoute && !queryParamsRemoved) {
                    this._previousHadQueryParams = hasQueryParams;
                    if (fragment !== this._previousFragment) {
                        this._previousFragment = fragment;
                        this.Scope.UpdateBreadcrumbFragment!(fragment);
                    }
                    return;
                }

                this.Scope.HandleNavigationEnd(event as NavigationEnd, this.sidebarItems());
      
                if (this.isTouchScreenDevice && this.sideNavOpen()) {
                    this.CloseTheSidePanel();
                }
                this.Scope.SetTabTemplateRef!(null);

                if (!isSameRoute || queryParamsRemoved) {
                    this.Scope.SetCurrentBreadCrumbRef!(null);
                    this.Scope.ClearCurrentBreadCrumbLabels!();
                }

                this._previousRoutePath = routePath;
                this._previousHadQueryParams = hasQueryParams;
                this._previousFragment = fragment;

                this.UpdateOverflowItems();
 
            }));
    }

    override ngOnInit(): void {
        super.ngOnInit();
        const storageKey = this.storageKey();
        if (storageKey && !this.isTouchScreenDevice) {
            const stored = Storage.GetValue(storageKey, true);
            if (stored) {
                this.sideNavOpen.set(true);
                this.onLinkTextChange.set(true);
            }
        }
    }

     ngAfterViewInit(): void {
        this._resizeObserver = new ResizeObserver(() => {
            this.sidebarItems().forEach(item => item.UpdateLabelTruncation());
            this.UpdateOverflowItems();
        });
        const sidebarEl = this.sidebarElRef()?.nativeElement;
        if (sidebarEl) {
            this._resizeObserver.observe(sidebarEl);
        }
        const sidebarLinksEl = this.sidebarLinksRef()?.nativeElement;
        if (sidebarLinksEl) {
            this._resizeObserver.observe(sidebarLinksEl);
        }

        // Run synchronously (not the rAF-debounced path) so the initial layout is correct before first paint, avoiding a flash of overflowing items.
        this.sidebarItems().forEach(item => {
            item.UpdateLabelTruncation();
            const labelEl = item.labelElRef()?.nativeElement;
            if (labelEl) {
                this._resizeObserver?.observe(labelEl);
            }
        });
        this.RecalculateOverflowItems();

        this._announcementResizeObserver = new ResizeObserver(() => {
            this.announcementHeight.set(this.announcementRef()?.nativeElement?.offsetHeight ?? 0);
        });
        const announcementEl = this.announcementRef()?.nativeElement;
        if (announcementEl) {
            this._announcementResizeObserver.observe(announcementEl);
        }
    }

    override ngOnDestroy(): void {
        clearTimeout(this._sidenavStateTimer);
        this._resizeObserver?.disconnect();
        this._announcementResizeObserver?.disconnect();
        if (this._overflowUpdateFrame !== null) {
            cancelAnimationFrame(this._overflowUpdateFrame);
        }
        super.ngOnDestroy();
    }

    /** Coalesces bursts of calls (effect + multiple ResizeObserver callbacks firing in the same tick) into a single recalculation per frame. */
    private UpdateOverflowItems(): void {
        if (this._overflowUpdateFrame !== null) return;
        this._overflowUpdateFrame = requestAnimationFrame(() => {
            this._overflowUpdateFrame = null;
            this.RecalculateOverflowItems();
        });
    }

    private RecalculateOverflowItems(): void {
        const linksEl = this.sidebarLinksRef()?.nativeElement;
        if (!linksEl) return;

        const items = this.sidebarItems();
        if (items.length === 0) {
            this.overflowItems.set([]);
            return;
        }

        // Separate visible and hidden items
        const visibleItems = items.filter(item => item.visibility() === NavigationItemState.Show);
        const hiddenItems = items.filter(item => item.visibility() !== NavigationItemState.Show);

        // Reveal visible items and hide hidden items
        items.forEach(item => {
            item.hostNativeElement.style.display = item.visibility() === NavigationItemState.Show ? '' : 'none';
        });

        const linksRect = linksEl.getBoundingClientRect();
        const linksBottom = linksRect.top + linksEl.clientHeight;

        // Check if all visible items fit
        if (visibleItems.length > 0) {
            const lastVisibleRect = visibleItems[visibleItems.length - 1].hostNativeElement.getBoundingClientRect();
            if (lastVisibleRect.bottom <= linksBottom && hiddenItems.length === 0) {
                // All items fit and no hidden items
                this.overflowItems.set([]);
                return;
            }
        }

        // Reserve space for the "More" button
        const moreItemHeight = (visibleItems.length > 0 ? visibleItems[0].hostNativeElement.getBoundingClientRect().height : items[0].hostNativeElement.getBoundingClientRect().height) || 48;
        const effectiveBottom = linksBottom - moreItemHeight;

        // Find the first visible item whose bottom exceeds the effective available space
        let cutoffIndex = visibleItems.length;
        let cutoffItemRect: DOMRect | null = null;
        for (let i = 0; i < visibleItems.length; i++) {
            const itemRect = visibleItems[i].hostNativeElement.getBoundingClientRect();
            if (itemRect.bottom > effectiveBottom) {
                cutoffIndex = i;
                cutoffItemRect = itemRect;
                break;
            }
        }

        // If only one visible item would overflow, check whether it fits in the space that "More" would occupy
        // (reuse the rect already read above instead of forcing another layout read for the same element)
        if (cutoffIndex === visibleItems.length - 1 && cutoffItemRect) {
            if (cutoffItemRect.bottom <= linksBottom && hiddenItems.length === 0) {
                this.overflowItems.set([]);
                return;
            }
        }

        // Hide the overflowing visible items
        for (let i = 0; i < visibleItems.length; i++) {
            visibleItems[i].hostNativeElement.style.display = i >= cutoffIndex ? 'none' : '';
        }

        // Build overflow items: visible items that overflow, then all hidden items
        const overflowVisibleItems = visibleItems.slice(cutoffIndex).map(item => this.ToOverflowItem(item));
        const hiddenOverflowItems = hiddenItems.map(item => this.ToOverflowItem(item));

        // Combine visible and hidden items
        const overflowList: OverflowSidebarItem[] = [...overflowVisibleItems, ...hiddenOverflowItems];
        this.overflowItems.set(overflowList);
    }

    private ToOverflowItem(item: SideBarItemComponent): OverflowSidebarItem {
        return {
            icon: item.icon(),
            navigationTitle: item.navigationTitle(),
            routerLink: item.routerLink(),
            badgeText: item.badgeText(),
            fragment: item.fragment(),
            queryParams: item.queryParams(),
            linkActive: item.GetLinkActiveState(),
            visibility: item.visibility()
        };
    }

    OnSidenavOpen() {
        if (!this.sideNavOpen()) {
            clearTimeout(this._sidenavAnimationTimer);
            this.sideNavClosing.set(false);
            this.sideNavOpen.set(true);
            this.SetSidenavStates();
        }
    }

    OnSidenavClose() {
        if (this.sideNavOpen()) {
            this.CloseTheSidePanel();
        }
    }

    OnRouteActivate(component: object): void {
        const componentType = component.constructor as Type<any>;
        this.routeAnimationState = this.Scope.ResolveRouteAnimation(componentType);
        this.Scope.SetCurrentComponentType(componentType);
    }

    ToggleSidenav() {
        if (this.sideNavOpen()) {
            this.CloseTheSidePanel();
        } else {
            this.OnSidenavOpen();
        }
    }

    CloseTheSidePanel() {
        this.sideNavOpen.set(false);
        if (this.isTouchScreenDevice) {
            clearTimeout(this._sidenavAnimationTimer);
            this.sideNavClosing.set(true);
            this._sidenavAnimationTimer = setTimeout(() => this.sideNavClosing.set(false), 250);
        }
        this.SetSidenavStates();
    }

    /** crumb.url is a path segment with queryParams and fragment stored separately, navigate with all three. */
    NavigateToBreadcrumb(crumb: HeaderBreadcrumb): void {
        this.Navigate(crumb.url, { queryParams: crumb.queryParams, fragment: crumb.fragment });
    }

    ForceDefaultAnimation() {
        this.Scope.ForceDefaultAnimation();
    }

    private SetSidenavStates(): void {
        const sideNavOpen = this.sideNavOpen();
        clearTimeout(this._sidenavStateTimer);
        if (!this.isTouchScreenDevice) {
            const storageKey = this.storageKey();
            if (storageKey) {
                Storage.SetValue(storageKey, sideNavOpen);
            }
            this._sidenavStateTimer = setTimeout(() => {
                this.onLinkTextChange.set(sideNavOpen);
            }, 200);
        } else {
            this.onLinkTextChange.set(sideNavOpen);
        }
    }


    get sideBarHidden(): boolean {
        return this.isTouchScreenDevice && !this.sideNavOpen() && !this.sideNavClosing();
    }



}
